import React, { useMemo, useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { useSelector } from 'react-redux';
import { useTranslation } from 'services/localization';
import { RiAddLine, RiSubtractLine } from 'react-icons/ri';
import { getOnboardingSteps } from 'services/onboarding/utils';
import OnboardingStep from './OnboardingStep';
import { getOnlineStoreUrl } from 'common/modules/urls';
import { getClientEnv } from 'common/utils/frontUtils';

type OnboardingSectionProps = {
	title: {
		text: string;
		variant: 'h4' | 'h6';
	};
	subtitle?: {
		text: string;
	};
};

const OnboardingSection = (props: OnboardingSectionProps) => {
	const { title, subtitle } = props;

	const theme = useTheme();
	const { t } = useTranslation();

	const shopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId)!;
	const shopUrls = useSelector(ShopSelectors.activeShopUrls);
	const isMultiLocationStore = Object.keys(shopUrls?.locationUrls ?? {}).length;

	const onboardingSteps = useMemo(() => {
		const onlineStoreUrl = getOnlineStoreUrl({
			env: getClientEnv(),
			locationId: isMultiLocationStore ? activeLocationId : null,
			shopUrlDoc: shopUrls,
		});
		return getOnboardingSteps({
			shopAnalytics,
			onlineStoreUrl,
			t,
		});
	}, [isMultiLocationStore, activeLocationId, shopUrls, shopAnalytics, t]);

	const showOnboardingAtAll = shopAnalytics?.showOnboarding === true;
	const allItemsCompleted = onboardingSteps.every((step) => step.items?.every((item) => item.done));
	const [showOnboarding, toggleShowOnboarding] = useState(
		showOnboardingAtAll && !allItemsCompleted,
	);

	return showOnboardingAtAll ? (
		<>
			<Box mb={!showOnboarding ? 4 : 2} mt={3}>
				{subtitle ? (
					<>
						<Typography variant={title.variant}>{title.text}</Typography>
						<Typography variant="body1" color="secondary" sx={{ mt: 2 }}>
							{subtitle.text}
						</Typography>
					</>
				) : (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<Typography variant={title.variant}>{title.text}</Typography>
						<Button
							variant="text"
							sx={{
								p: 0,
								justifyContent: 'flex-end',
								'&:hover': {
									backgroundColor: 'transparent',
								},
								color: theme.palette.primary.light,
							}}
							disableRipple={true}
							onClick={() => toggleShowOnboarding(!showOnboarding)}
						>
							{showOnboarding ? (
								<>
									{t('homeView.onboarding.hide', 'Hide')}
									<RiSubtractLine color={theme.palette.primary.main} />
								</>
							) : (
								<>
									{t('homeView.onboarding.show', 'Show')}
									<RiAddLine color={theme.palette.primary.main} />
								</>
							)}
						</Button>
					</Box>
				)}
			</Box>
			{showOnboarding &&
				onboardingSteps.map((step, index) => {
					const firstNotCompletedStep = onboardingSteps.find((step) =>
						step.items.some((item) => !item.done),
					);
					return (
						<OnboardingStep
							key={step.name + index}
							stepName={step.name}
							items={step.items}
							isStepOpen={firstNotCompletedStep?.name === step.name}
							isLastStep={index === onboardingSteps.length - 1}
						/>
					);
				})}
		</>
	) : null;
};

export default OnboardingSection;
